import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	Col,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	Row,
	UncontrolledDropdown,
} from 'reactstrap';

import { sortByWeight } from '@techchange/dnd';
import { messages } from '../../../../_shared/i18n/messages';

import { FORM_TYPES } from '../../../constants';
import EmbeddedForm from '../../EmbeddedForm';
import { useFormEditorContext } from '../contexts/FormEditorContext';

import './FormEditorPreview.scss';

const propTypes = {

	/**
	 * The list of all the tickets associated with the product.
	 */
	tickets: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The ticket ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The ticket name.
		 */
		title: PropTypes.string.isRequired,

	})),

	/**
	 * The the list of all the ticket groups associated with the product.
	 */
	ticketGroups: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The ticket group ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The ticket name.
		 */
		title: PropTypes.string.isRequired,

	})),

	/**
	 * Callback function to generate the form to preview.
	 */
	onPreview: PropTypes.func.isRequired,

};

/**
 * Provides a preview of the form, including
 * conditional navigation logic, while editing.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}  The component to render.
 */
const FormEditorPreview = (props) => {

	const {
		tickets,
		ticketGroups,
		onPreview,
	} = props;

	const intl = useIntl();

	const { defaultLanguage, selectedLanguage } = useFormEditorContext();

	const form = onPreview();

	const [previewUserTicket, setPreviewUserTicket] = useState(tickets ? tickets[0] : null);
	const [previewUserTicketGroup, setPreviewUserTicketGroup] =
		useState(ticketGroups ? ticketGroups[0] : null);

	const isPaginated = form.type === FORM_TYPES.APPLICATION;
	const hasTickets = tickets && tickets.length > 0;
	const hasTicketGroups = ticketGroups && ticketGroups.length > 0;

	let previewUserTicketTitle = previewUserTicket ? previewUserTicket.title : '';

	if (previewUserTicket && !previewUserTicket.active) {
		previewUserTicketTitle = `${previewUserTicket.title} (${intl.formatMessage(messages.TC_COMPONENTS_GENERAL_INACTIVE_LABEL)})`;
	}

	const sortedTickets = sortByWeight(tickets);

	sortedTickets.sort((ticket1, ticket2) => ticket2.active - ticket1.active);

	const sortedTicketGroups = sortByWeight(ticketGroups);

	return (
		<Row>
			{isPaginated && (hasTickets || hasTicketGroups) &&
				<Col
					xs="12"
					className="form-preview-settings">
					{hasTickets &&
						<div className="d-flex align-items-center mb-2">
							<Col xs="3">
								<b>
									<FormattedMessage
										{...messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_LABEL_PREVIEW_TICKET_TYPE} />
								</b>
							</Col>
							<Col>
								<UncontrolledDropdown>
									<DropdownToggle
										className="nav-dropdown-toggle"
										title={previewUserTicketTitle}
										color="neutral"
										outline>
										<span className="nav-dropdown-toggle-label">
											{previewUserTicketTitle}
										</span>
										<span>
											<i
												className="fas fa-caret-down ms-1"
												aria-hidden="true" />
										</span>
									</DropdownToggle>
									<DropdownMenu>
										{sortedTickets.map((ticket) => {

											const ticketTitle = `${ticket.title} ${ticket.active
												? ''
												: `(${intl.formatMessage(messages.TC_COMPONENTS_GENERAL_INACTIVE_LABEL)})`}`;

											return (
												<DropdownItem
													key={ticket.id}
													title={ticketTitle}
													onClick={() => setPreviewUserTicket(ticket)}>
													{ticketTitle}
												</DropdownItem>
											);

										})}
									</DropdownMenu>
								</UncontrolledDropdown>
							</Col>
						</div>
					}
					{hasTicketGroups &&
						<div className="d-flex align-items-center mb-2">
							<Col xs="3">
								<b>
									<FormattedMessage
										{...messages.TC_COMPONENTS_FORM_EDITOR_BUTTON_LABEL_PREVIEW_TICKET_GROUP} />
								</b>
							</Col>
							<Col>
								<UncontrolledDropdown>
									<DropdownToggle
										className="nav-dropdown-toggle"
										title={previewUserTicketGroup.title}
										color="neutral"
										outline>
										<span className="nav-dropdown-toggle-label">
											{previewUserTicketGroup.title}
										</span>
										<span>
											<i
												className="fas fa-caret-down ms-1"
												aria-hidden="true" />
										</span>
									</DropdownToggle>
									<DropdownMenu>
										{sortedTicketGroups.map((ticketGroup) => {
											return (
												<DropdownItem
													key={ticketGroup.id}
													title={ticketGroup.title}
													onClick={() => setPreviewUserTicketGroup(ticketGroup)}>
													{ticketGroup.title}
												</DropdownItem>
											);
										})}
									</DropdownMenu>
								</UncontrolledDropdown>
							</Col>
						</div>
					}
				</Col>
			}
			<Col xs="12">
				<EmbeddedForm
					form={backfillContentDefaultLanguage(form, defaultLanguage, selectedLanguage)}
					userTicket={previewUserTicket}
					userTicketGroup={previewUserTicketGroup}
					isPaginated={isPaginated}
					isEditing
					isPreview />
			</Col>
		</Row>
	);

};

FormEditorPreview.propTypes = propTypes;

export default FormEditorPreview;

/**
 * Backfill content in the default language for the form required
 * for previewing with the selected language as the primary choice.
 *
 * @param {object} data  The current state of the form data.
 * @param {string} defaultLanguage  The default language for the form.
 * @param {string} selectedLanguage  The selected language for the form.
 *
 * @returns {object} result  The result.
 */
function backfillContentDefaultLanguage(data, defaultLanguage, selectedLanguage) {

	const formContentDefault =
		data.form_contents.find((content) => content.language === defaultLanguage);

	const formContentSelected =
		data.form_contents.find((content) => content.language === selectedLanguage);

	return {
		...data,
		form_contents: [{
			language: defaultLanguage,
			name:
				formContentSelected?.name ||
				formContentDefault?.name,
			description:
				formContentSelected?.description ||
				formContentDefault?.description,
		}],
		form_fields: data.form_fields?.map((field) => {

			const fieldContentDefault =
				field.form_field_contents.find((content) => content.language === defaultLanguage);

			const fieldContentSelected =
				field.form_field_contents.find((content) => content.language === selectedLanguage);

			return {
				...field,
				form_field_contents: [{
					language: defaultLanguage,
					title:
						fieldContentSelected?.title ||
						fieldContentDefault?.title,
					placeholder:
						fieldContentSelected?.placeholder ||
						fieldContentDefault?.placeholder,
					help:
						fieldContentSelected?.help ||
						fieldContentDefault?.help,
					submission_feedback_correct:
						fieldContentSelected?.submission_feedback_correct ||
						fieldContentDefault?.submission_feedback_correct,
					submission_feedback_incorrect:
						fieldContentSelected?.submission_feedback_incorrect ||
						fieldContentDefault?.submission_feedback_incorrect,
				}],
				form_field_options: field.form_field_options?.map((option) => {

					const optionContentDefault =
						option.form_field_option_contents.find(
							(content) => content.language === defaultLanguage
						);

					const optionContentSelected =
						option.form_field_option_contents.find(
							(content) => content.language === selectedLanguage
						);

					return {
						...option,
						form_field_option_contents: [{
							language: defaultLanguage,
							label: optionContentSelected?.label || optionContentDefault?.label,
						}],
					};

				}),
			};

		}),
		form_sections: data.form_sections?.map((section) => {

			const sectionContentDefault =
				section.form_section_contents.find(
					(content) => content.language === defaultLanguage
				);

			const sectionContentSelected =
				section.form_section_contents.find(
					(content) => content.language === selectedLanguage
				);

			return {
				...section,
				form_section_contents: [{
					language: defaultLanguage,
					title:
						sectionContentSelected?.title ||
						sectionContentDefault?.title,
					description:
						sectionContentSelected?.description ||
						sectionContentDefault?.description,
				}],
			};

		}),
	};

}
